import { ProjectFileNode } from "../shared/ProjectFileNode";

/**
 * A single drive item (file or folder) returned by Microsoft Graph when browsing a
 * project's SharePoint Online folders, adapted into the same shape used by
 * ProjectFilesBrowser/ProjectFileItem so the existing folder-browsing UI can be reused.
 */
export interface SharePointFolderItem {
    /** Graph drive item ID. */
    id: string;
    name: string;
    type: "file" | "folder";
    /** ISO 8601, from Graph's lastModifiedDateTime. */
    lastModified: string;
    /** SharePoint web URL, for a potential "open in browser" affordance. */
    webUrl?: string;
    /** Drive ID needed for subsequent Graph calls when navigating into this item. */
    parentDriveId: string;
    /** From Graph `file.mimeType`; folders have none. */
    mimeType?: string;
    sizeInBytes?: number;
}

/** Location of a Graph drive item; `itemId` null means the drive (document library) root. */
export interface SharePointDriveLocation {
    driveId: string;
    itemId: string | null;
}

/** One page of a folder listing; `nextLink` is the Graph continuation URL when more pages exist. */
export interface SharePointFolderPage {
    items: SharePointFolderItem[];
    nextLink?: string;
}

const SHAREPOINT_ITEM_NRN_PREFIX = "nrn:file:sharepoint:";

function buildSharePointItemNrn(driveId: string, itemId: string): string {
    return `${SHAREPOINT_ITEM_NRN_PREFIX}${driveId}:${itemId}`;
}

/** Adapts a Graph drive item to the node shape ProjectFilesBrowserRow renders. */
export function toSharePointNode(item: SharePointFolderItem, connectionId: string): ProjectFileNode {
    return {
        id: buildSharePointItemNrn(item.parentDriveId, item.id),
        name: item.name,
        type: item.type,
        mimeType: item.mimeType,
        source: "sharepoint",
        connectionId,
    };
}
